import { useCallback, useEffect, useRef, useState } from 'react';
import { getEmployeeLifecycle, changeEmployeeLifecycle, retryLifecycleOperation } from '../api/client';
import type { LifecycleStatus } from '../api/client';
import {
  offeredAction, validateReason, confirmCopy, stateHeadline, loginLine, enforcementNote, retryOffer, reservationNote, failureMessage, REASON_MAX,
} from './lifecycleModel';
import type { Kind, Tone } from './lifecycleModel';

// BUILD-039 Deactivate / Reactivate — the "Account status" section of the Manage panel.
// The server decides who may act and what happened; this component only shows what it returned. It never sends an actor, never
// claims success the server did not report, and a refused or failed request leaves the status exactly as the server last said.

const TONE_CLASS: Record<Tone, string> = {
  ok: 'text-green-700', info: 'text-gray-700', warn: 'text-amber-700', error: 'text-red-700',
};

export default function LifecyclePanel({
  employeeId, name, globalStatus, onChanged,
}: {
  employeeId: string;
  name: string;
  globalStatus: 'active' | 'disabled' | null;
  /** Called after any change so the Directory reloads its Employee list (Show inactive and counts follow the server). */
  onChanged: () => void;
}) {
  const [status, setStatus] = useState<LifecycleStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Kind | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getEmployeeLifecycle(employeeId);
      setStatus(s);
      setLoadError(null);
      return s;
    } catch (e) {
      setLoadError(failureMessage(e));
      return null;
    }
  }, [employeeId]);

  useEffect(() => { void refresh(); }, [refresh, globalStatus]);
  useEffect(() => { if (confirming && reasonRef.current) reasonRef.current.focus(); }, [confirming]);

  // The Directory's own status is authoritative for which button to offer; the lifecycle read model supplies the operation detail.
  const shownStatus = status ? status.globalStatus : globalStatus; // the lifecycle read model is the server's own answer; fall back to the Directory's
  const action = offeredAction(shownStatus);
  const headline = stateHeadline(status);
  const enforcement = enforcementNote(status);
  const retry = retryOffer(status);
  const pausedNote = reservationNote(status);
  const kindForLines: Kind | null = status && status.operation ? (status.operation.kind as Kind) : null;

  async function run(kind: Kind) {
    const v = validateReason(reason);
    if (!v.ok) { setReasonError(v.message); return; }
    setReasonError(null);
    setBusy(kind);
    setNotice({ tone: 'info', text: kind === 'deactivate' ? 'Deactivating… this can take a few seconds.' : 'Reactivating…' });
    try {
      const res = await changeEmployeeLifecycle(employeeId, kind, v.reason);
      if (res.status) setStatus(res.status);
      if (res.outcome === 'finished' && res.state === 'complete') {
        setNotice({ tone: 'ok', text: kind === 'deactivate' ? 'Deactivated.' : 'Reactivated.' });
      } else if (res.outcome === 'finished') {
        setNotice({ tone: 'warn', text: 'Finished, but not everything completed cleanly. Details are below.' });
      } else {
        setNotice({ tone: 'warn', text: res.message || 'The change was interrupted. Some steps may already have happened. Details are below.' });
      }
      setConfirming(null); setReason('');
    } catch (e) {
      // A refusal or an unreachable service: nothing is claimed. Re-read the real state so the screen cannot show a guess.
      setNotice({ tone: 'error', text: failureMessage(e) });
      await refresh();
    } finally {
      setBusy(null);
      onChanged();
    }
  }

  async function doRetry(operationId: string) {
    setBusy('retry');
    setNotice({ tone: 'info', text: 'Retrying…' });
    try {
      const res = await retryLifecycleOperation(operationId);
      if (res.status) setStatus(res.status);
      setNotice(res.outcome === 'finished' && res.state === 'complete'
        ? { tone: 'ok', text: 'Finished.' }
        : { tone: 'warn', text: res.message || 'Retried. Not everything completed; details are below.' });
    } catch (e) {
      setNotice({ tone: 'error', text: failureMessage(e) });
      await refresh();
    } finally {
      setBusy(null);
      onChanged();
    }
  }

  const copy = confirming ? confirmCopy(confirming, name) : null;

  return (
    <section aria-label="Account status" data-testid="lifecycle-panel" className="mt-4 pt-3 border-t">
      <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Account status</h5>
      <p className="text-xs mb-2" data-testid="lifecycle-status-line">
        {shownStatus === 'disabled'
          ? <span className="inline-block px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-medium">Inactive</span>
          : shownStatus === 'active'
            ? <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">Active</span>
            : <span className="text-gray-500">Status unknown</span>}
      </p>

      {loadError && <p role="alert" className="text-xs text-red-700 mb-2">Could not read the account status: {loadError}</p>}

      {!confirming && action && (
        <button
          type="button"
          data-testid={action === 'deactivate' ? 'lifecycle-deactivate' : 'lifecycle-reactivate'}
          onClick={() => { setConfirming(action); setReason(''); setReasonError(null); setNotice(null); }}
          disabled={!!busy}
          className={`px-2.5 py-1 text-xs font-medium rounded-md border ${action === 'deactivate' ? 'border-gray-300 text-red-600 hover:bg-red-50' : 'border-gray-300 text-blue hover:bg-gray-100'} disabled:opacity-50`}
          title={action === 'deactivate' ? 'Stops this person using any app. Nothing is deleted.' : 'Shows this person as active again. Only sign-ins this change turned off are restored.'}
        >
          {action === 'deactivate' ? 'Deactivate…' : 'Reactivate…'}
        </button>
      )}

      {confirming && copy && (
        <div role="group" aria-label={copy.title} data-testid="lifecycle-confirm" className="border rounded-md p-3 bg-gray-50">
          <p className="text-sm font-medium text-navy mb-1">{copy.title}</p>
          <p className="text-xs text-gray-700 mb-2">{copy.body}</p>
          <label htmlFor={`lifecycle-reason-${employeeId}`} className="block text-xs font-medium text-gray-700 mb-1">Reason (required)</label>
          <textarea
            id={`lifecycle-reason-${employeeId}`}
            ref={reasonRef}
            data-testid="lifecycle-reason"
            value={reason}
            maxLength={REASON_MAX}
            rows={2}
            onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
            aria-invalid={!!reasonError}
            aria-describedby={reasonError ? `lifecycle-reason-error-${employeeId}` : undefined}
            className="w-full px-2 py-1 border rounded text-sm"
            placeholder="For example: left the company"
            disabled={!!busy}
          />
          {reasonError && <p id={`lifecycle-reason-error-${employeeId}`} role="alert" className="text-xs text-red-700 mt-1">{reasonError}</p>}
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              data-testid="lifecycle-confirm-button"
              onClick={() => void run(confirming)}
              disabled={!!busy}
              className={`px-3 py-1 rounded text-xs font-medium text-white ${confirming === 'deactivate' ? 'bg-red-600' : 'bg-blue'} disabled:opacity-50`}
            >
              {busy ? 'Working…' : copy.button}
            </button>
            <button type="button" onClick={() => { setConfirming(null); setReasonError(null); }} disabled={!!busy} className="px-3 py-1 border rounded text-xs disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}

      {notice && <p role="status" data-testid="lifecycle-notice" className={`text-xs mt-2 ${TONE_CLASS[notice.tone]}`}>{notice.text}</p>}

      {pausedNote && <p role="status" data-testid="lifecycle-paused-note" className="text-xs mt-2 text-amber-700">{pausedNote}</p>}

      {headline && (
        <div className="mt-3" data-testid="lifecycle-result">
          <p className={`text-xs font-medium ${TONE_CLASS[headline.tone]}`}>{headline.text}</p>
          {status && status.operation && status.operation.reason && (
            <p className="text-xs text-gray-500 mt-0.5">Reason recorded: {status.operation.reason}</p>
          )}
          {enforcement && <p className="text-xs text-gray-600 mt-1">{enforcement}</p>}
          {status && status.logins && status.logins.length > 0 && (
            <ul className="mt-2 space-y-1" aria-label="Per-app result">
              {status.logins.map((l, i) => {
                const line = loginLine(l, kindForLines);
                return (
                  <li key={`${l.app}-${l.account}-${i}`} className="text-xs" data-testid="lifecycle-login-line">
                    <span className="font-medium text-gray-800">{line.app}</span>
                    <span className="text-gray-400"> ({l.account})</span>
                    <span className={`block ${TONE_CLASS[line.tone]}`}>{line.text}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {status && status.discoveredLogins && status.discoveredLogins.length > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              Another sign-in was found that this change has not handled yet: {status.discoveredLogins.map((d) => `${d.app} (${d.account})`).join(', ')}. Retry will include it.
            </p>
          )}
          {retry.available && retry.operationId && (
            <div className="mt-2">
              <button type="button" data-testid="lifecycle-retry" onClick={() => void doRetry(retry.operationId!)} disabled={!!busy}
                className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-blue hover:bg-gray-100 disabled:opacity-50">
                {busy === 'retry' ? 'Retrying…' : 'Retry'}
              </button>
              <p className="text-xs text-gray-500 mt-1">{retry.note}</p>
            </div>
          )}
          {!retry.available && retry.note && <p className="text-xs text-gray-500 mt-1">{retry.note}</p>}
        </div>
      )}

      {status && status.history && status.history.length > 0 && (
        <div className="mt-3" data-testid="lifecycle-history">
          <h6 className="text-xs font-semibold text-gray-600 mb-1">Change history (kept)</h6>
          <ul className="space-y-0.5">
            {status.history.map((h) => (
              <li key={h.operationId} className="text-xs text-gray-600">
                {h.kind === 'reactivate' ? 'Reactivated' : 'Deactivated'} · {h.state.replace(/-/g, ' ')} · {new Date(h.requestedAt).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
